import {
  Box,
  makeStyles,
  Theme,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@freighthub/silhouette-core'
import classNames from 'classnames'
import { isNil } from 'lodash-es'
import { FC } from 'react'

import { TransportModeWithShipIcon, TTransportModeWithShip } from '../QuoteSheet/QuoteOption/QuoteTransportPlan'
import { TransportPlanItemDescription, TransportPlanItemDescriptionProps } from './TransportPlanItemDescription'

type BookingTimelineItemProps = {
  icon?: TTransportModeWithShip
  isLastLeg?: boolean
  isFirstLeg?: boolean
  timelineContent?: TransportPlanItemDescriptionProps
}
type StyleProps = {
  isIcon: boolean
}

export const bookingTimelineItemTestIds = {
  dot: 'booking_timeline_item-dot',
  connectorUp: 'booking_timeline_item-connector_up',
  connectorDown: 'booking_timeline_item-connector_down',
  icon: 'booking_timeline_item-icon',
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  dotContainer: {
    width: 36,
    display: 'flex',
    justifyContent: 'center',
  },
  dot: {
    backgroundColor: ({ isIcon }) => (isIcon ? theme.palette.header.background : theme.palette.primary.main),
    color: theme.palette.common.white,
  },
  line: {
    backgroundColor: theme.palette.primary.main,
  },
  hiddenLine: {
    backgroundColor: 'transparent',
  },
  timelineItem: {
    '&:before': {
      display: 'none',
    },
    '&:nth-child(2n ) $timelineContent': {
      backgroundColor: theme.palette.background.default,
    },
  },
  separator: {
    backgroundColor: theme.palette.tag.info,
    flex: 'none',
    width: 150,
  },
  timelineContent: {
    padding: 0,
  },
}))

export const BookingTimelineItem: FC<BookingTimelineItemProps> = ({
  icon,
  isLastLeg = false,
  isFirstLeg = false,
  timelineContent,
}) => {
  const classes = useStyles({ isIcon: Boolean(icon) })

  return (
    <TimelineItem className={classes.timelineItem}>
      <TimelineSeparator className={classes.separator}>
        <TimelineConnector
          data-testid={bookingTimelineItemTestIds.connectorUp}
          className={classNames(classes.line, { [classes.hiddenLine]: isFirstLeg })}
        />
        <Box className={classes.dotContainer}>
          <TimelineDot data-testid={bookingTimelineItemTestIds.dot} className={classes.dot}>
            {icon && <TransportModeWithShipIcon transportMode={icon} data-testid={bookingTimelineItemTestIds.icon} />}
          </TimelineDot>
        </Box>
        <TimelineConnector
          data-testid={bookingTimelineItemTestIds.connectorDown}
          className={classNames(classes.line, { [classes.hiddenLine]: isLastLeg })}
        />
      </TimelineSeparator>

      <TimelineContent className={classes.timelineContent}>
        {!isNil(timelineContent) && <TransportPlanItemDescription {...timelineContent} />}
      </TimelineContent>
    </TimelineItem>
  )
}
